/* eslint-disable react/no-unescaped-entities */
import { FunctionComponent, useEffect, useRef, useState, useCallback, useMemo } from 'react';
import useNonInitialEffect from '@versiondos/hooks';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { Spinner } from 'components/UI';
import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons';
import { AlertNewBooking } from 'components/CreateBooking/UI/AlertNewBooking';
import { BoardingServiceWrapper, BoardingService } from 'model/Service';
import { fetchBoardingOffersThunk } from 'actions/services/ServicesActions';
import { useApiClientWrapper } from 'hooks';
import { ServicesStatus } from 'reducers/services/ServicesState';
import { addTimeToDate } from 'utils/DateUtils';
import styles from './BoardingServiceSelector.module.scss';
import { Availability } from '../Availability/Availability';
import StandardRateSelector from '../StandardRateSelector/StandardRateSelector';
import { Moment } from 'moment-timezone';
import { Room } from '@spike/room-model';
import type { MembershipItem } from '@spike/membership-model';
import { parseMembershipServiceId } from 'components/CreateBooking/Memberships/utils/membershipItemFlow';
import { getBoardingCatalogNightlyPrice } from '../../utils/boardingPricing';
import { canFetchBoardingOffers } from 'utils/boardingOfferDates';

interface Props {
    petId: number;
    from: Moment;
    to: Moment;
    edit: boolean;
    selectedService?: BoardingServiceWrapper;
    room?: Room;
    selectedRoomUnitId?: number;
    nightlyPrice?: number;
    nightlyPriceError?: string;
    saveCustomPrice?: boolean;
    useSavedCustomPrice?: boolean;
    readOnly?: boolean;
    nights?: number;
    servicesSelected?: Array<number>;
    onSelectService: (service: BoardingServiceWrapper) => void;
    onNightlyPriceChange: (price: number) => void;
    onSaveCustomPriceChange?: (checked: boolean) => void;
    onUseSavedCustomPriceChange?: (useSaved: boolean, price: number, customPriceId?: number) => void;
    onChangePeriod: (from: Moment, to: Moment) => void;
    onSelectRoomUnitId: (roomUnitId: number | undefined) => void;
    notes?: string;
    onNotesChange?: (notes: string) => void;
    onUnitChange?: (unitId: number, from: Moment) => void;
    membershipItem?: MembershipItem;
    boardingMembershipAlertServiceId?: number;
}

export const BoardingServiceSelector: FunctionComponent<Props> = props => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();

    const ref = useRef<HTMLDivElement>(null);
    const prevServiceIdForRoomUnitRef = useRef<number | undefined>(undefined);

    const [loading, setLoading] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);
    const [baseDates, setBaseDates] = useState<{ from: Moment; to: Moment }>({
        from: props.from.clone(),
        to: props.to.clone()
    });

    const boardingServices = useSelector<RootState, Array<BoardingService>>(
        state => state.services.boardingServices || []
    );

    const boardingServicesStatus = useSelector<RootState, ServicesStatus>(state => state.services.status);

    const selectedRoom = useMemo(() => {
        return props.selectedService?.rooms?.[0] ?? props.room;
    }, [props.room, props.selectedService]);

    const membershipBoardingServiceId = useMemo(() => {
        if (props.boardingMembershipAlertServiceId != null) {
            return props.boardingMembershipAlertServiceId;
        }
        const item = props.membershipItem ?? props.selectedService?.membershipItem;
        return item != null ? parseMembershipServiceId(item) : undefined;
    }, [props.boardingMembershipAlertServiceId, props.membershipItem, props.selectedService?.membershipItem]);

    const services: Array<BoardingServiceWrapper> = useMemo(
        () =>
            boardingServices
                .map(service => {
                    const checkInDateTime = service.checkInTime?.start
                        ? addTimeToDate(props.from, service.checkInTime.start)
                        : undefined;
                    const checkOutDateTime = service.checkOutTime?.start
                        ? addTimeToDate(props.to, service.checkOutTime.start)
                        : undefined;
                    const catalogNightly = getBoardingCatalogNightlyPrice(service);

                    return {
                        ...service,
                        durationService: {
                            hours: Math.trunc(service.duration / 60),
                            minutes: service.duration % 60
                        },
                        beforePrice: catalogNightly,
                        price: service.price,
                        serviceColor: service.serviceColor,
                        latePickupFee: service.latePickupFee,
                        checkInTime: service.checkInTime,
                        checkOutTime: service.checkOutTime,
                        checkInDateTime,
                        checkOutDateTime,
                        serviceItems: service.serviceItems,
                        rooms: service.rooms,
                        minimumNights: service.minimumNights,
                        offers: service.offers
                    };
                })
                .sort((wrapper, otherWrapper) =>
                    wrapper.serviceName.toLowerCase().localeCompare(otherWrapper.serviceName.toLowerCase())
                ),
        [boardingServices, props.from, props.to]
    );

    const servicesFiltered = useMemo(
        () =>
            services.filter(service => {
                const serviceMinNights = service.minimumNights ?? 0;
                const propsNights = props.nights ?? 1;
                return serviceMinNights <= propsNights;
            }),
        [services, props.nights]
    );

    const boardingOfferDates = useMemo(() => {
        if (!canFetchBoardingOffers(props.petId, props.from, props.to)) {
            return null;
        }
        return {
            startDate: props.from.format('YYYY-MM-DD'),
            endDate: props.to.format('YYYY-MM-DD')
        };
    }, [props.petId, props.from, props.to]);

    const getServicesUnused = useCallback(() => {
        if (props.servicesSelected) {
            const servicesToSelect: Array<BoardingServiceWrapper> = [];
            servicesFiltered.forEach(serv => {
                const serviceFounded = props.servicesSelected?.find(s => s === serv?.serviceId);
                if (!serviceFounded) {
                    servicesToSelect.push(serv);
                }
            });
            return servicesToSelect;
        }
        return servicesFiltered;
    }, [servicesFiltered, props.servicesSelected]);

    const refetchBoardingOffers = useCallback(() => {
        if (boardingOfferDates == null) {
            return;
        }
        dispatch(
            fetchBoardingOffersThunk(
                props.petId,
                boardingOfferDates.startDate,
                boardingOfferDates.endDate,
                apiClientWrapper.marketplaceId
            )
        );
    }, [dispatch, props.petId, boardingOfferDates, apiClientWrapper.marketplaceId]);

    useEffect(() => {
        refetchBoardingOffers();
    }, [refetchBoardingOffers]);

    useEffect(() => {
        if (props.selectedService && !isInitialized) {
            setBaseDates({
                from: props.from.clone(),
                to: props.to.clone()
            });
            setIsInitialized(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.selectedService]);

    useEffect(() => {
        if (!props.readOnly) {
            setTimeout(() => {
                ref?.current?.scrollIntoView();
            }, 2000);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ref, props.petId]);

    useNonInitialEffect(() => {
        switch (boardingServicesStatus) {
            case ServicesStatus.FetchingBoardingOffers:
                setLoading(true);
                break;
            case ServicesStatus.FetchBoardingOffersSuccess:
            case ServicesStatus.Error:
                setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [boardingServicesStatus]);

    useEffect(() => {
        if (props.selectedService || props.edit || servicesFiltered.length === 0) {
            return;
        }
        if (membershipBoardingServiceId != null) {
            const membershipService = servicesFiltered.find(s => s.serviceId === membershipBoardingServiceId);
            if (!membershipService) {
                return;
            }
            props.onSelectService(membershipService);
            return;
        }
        props.onSelectService(servicesFiltered[0]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [servicesFiltered, membershipBoardingServiceId]);

    useEffect(() => {
        if (!props.selectedService?.serviceId || !isInitialized) {
            return;
        }
        const serviceId = props.selectedService.serviceId;
        const prevId = prevServiceIdForRoomUnitRef.current;
        if (prevId != null && prevId !== serviceId) {
            props.onSelectRoomUnitId(undefined);
        }
        prevServiceIdForRoomUnitRef.current = serviceId;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.selectedService?.serviceId, isInitialized]);

    useEffect(() => {
        if (props.edit && props.selectedService && props.room && props.selectedRoomUnitId) {
            if (props.room.units && props.room.units.length > 0) {
                const roomUnitToSelect = props.room.units.find(unit => unit.id === props.selectedRoomUnitId);
                if (roomUnitToSelect) {
                    if (props.selectedRoomUnitId !== roomUnitToSelect.id) {
                        props.onSelectRoomUnitId(roomUnitToSelect.id);
                    }
                } else {
                    props.onSelectRoomUnitId(undefined);
                }
            }
        }
    }, [props.edit, props.selectedService, props.room, props.selectedRoomUnitId, props.onSelectRoomUnitId]);

    const emptyServicesComponent = (
        <div id="booking_div_no_services_alert">
            <AlertNewBooking
                className={styles.alert}
                bWidth="2px"
                icon={faExclamationTriangle}
                iconSize="lg"
                iconColor="#EAB464"
                bgColor="#F8F5F1"
                bdColor="#BCB8AE"
            >
                <span className={styles.alertText}>No services available for the selected number of nights.</span>
            </AlertNewBooking>
        </div>
    );

    const renderContent = () => {
        return getServicesUnused()?.length === 0 ? (
            emptyServicesComponent
        ) : (
            <StandardRateSelector
                boardingServices={servicesFiltered}
                from={props.from}
                to={props.to}
                nights={props.nights}
                selectedService={props.selectedService}
                room={selectedRoom}
                roomUnitId={props.selectedRoomUnitId}
                nightlyPrice={props.nightlyPrice}
                nightlyPriceError={props.nightlyPriceError}
                notes={props.notes}
                saveCustomPrice={props.saveCustomPrice}
                useSavedCustomPrice={props.useSavedCustomPrice}
                onChangePeriod={props.onChangePeriod}
                onSelect={props.onSelectService}
                onNightlyPriceChange={props.onNightlyPriceChange}
                onSaveCustomPriceChange={props.onSaveCustomPriceChange}
                onUseSavedCustomPriceChange={props.onUseSavedCustomPriceChange}
                onSelectRoomUnitId={props.onSelectRoomUnitId}
                onNotesChange={props.onNotesChange}
                petId={props.petId}
                isLoading={false}
                membershipItem={props.membershipItem ?? props.selectedService?.membershipItem}
                membershipBoardingServiceId={membershipBoardingServiceId}
            />
        );
    };

    return (
        <div className={styles.container}>
            {loading ? (
                <Spinner />
            ) : (
                <div ref={ref}>
                    <div className={styles.tabs}>
                        <span className={styles.activeTab}>Standard Rates</span>
                    </div>

                    <div style={{ margin: '14px' }}>
                        <Availability
                            from={props.from}
                            to={props.to}
                        />
                    </div>
                    {renderContent()}
                </div>
            )}
        </div>
    );
};

export default BoardingServiceSelector;
